import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, AlertTriangle, CheckCircle2, Circle } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  getProjectMeta,
  riskClassLabel,
  safetyChecklistFor,
  safetyProgress,
  setRiskClass,
  toggleSafetyItem,
  type ProjectMeta,
  type RiskClass,
} from "@/lib/prometheusProjectMeta";

const RISK_OPTIONS: RiskClass[] = [
  "low",
  "electrical",
  "structural",
  "chemical",
  "high_power",
];

export type ArtifactSafetyPanelProps = {
  projectId: string;
  projectTitle: string;
  discipline?: string;
  className?: string;
};

export function ArtifactSafetyPanel({
  projectId,
  projectTitle,
  discipline,
  className,
}: ArtifactSafetyPanelProps) {
  const [meta, setMeta] = useState<ProjectMeta>(() =>
    getProjectMeta(projectId, projectTitle, discipline)
  );

  const reload = useCallback(() => {
    setMeta(getProjectMeta(projectId, projectTitle, discipline));
  }, [projectId, projectTitle, discipline]);

  useEffect(() => {
    reload();
    const sync = () => reload();
    window.addEventListener("prometheus-project-meta-changed", sync);
    return () => window.removeEventListener("prometheus-project-meta-changed", sync);
  }, [reload]);

  const items = safetyChecklistFor(meta.riskClass);
  const prog = safetyProgress(projectId, projectTitle, discipline);

  return (
    <div className={cn("space-y-4 font-mono-tech", className)}>
      <div className="flex items-start gap-2">
        <ShieldCheck className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
        <div>
          <p className="text-[10px] uppercase tracking-[0.14em] text-amber-400 font-bold">
            Seguridad · risk_class
          </p>
          <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
            Checklist del proyecto antes de energizar, USB o Monitor. Sin XP — solo evidencia.
          </p>
        </div>
      </div>

      <label className="block space-y-1">
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">
          Clase de riesgo
        </span>
        <select
          value={meta.riskClass}
          onChange={(e) => {
            setMeta(setRiskClass(projectId, e.target.value as RiskClass, projectTitle, discipline));
          }}
          className="w-full rounded-lg border border-border bg-background px-2.5 py-2 text-[12px] text-foreground cursor-pointer"
        >
          {RISK_OPTIONS.map((rc) => (
            <option key={rc} value={rc}>
              {riskClassLabel(rc)}
            </option>
          ))}
        </select>
      </label>

      <div className="flex items-center justify-between text-[11px]">
        <span className="text-muted-foreground">
          {prog.done}/{prog.total} · críticos OK: {prog.allCriticalOk ? "sí" : "no"}
        </span>
        {!prog.allCriticalOk ? (
          <span className="inline-flex items-center gap-1 text-amber-400">
            <AlertTriangle className="w-3 h-3" />
            Faltan críticos
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-emerald-400">
            <CheckCircle2 className="w-3 h-3" />
            Listo a energizar
          </span>
        )}
      </div>

      <ul className="space-y-1.5">
        {items.map((item) => {
          const done = !!meta.safetyDone[item.id];
          return (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => {
                  setMeta(toggleSafetyItem(projectId, item.id, projectTitle, discipline));
                }}
                className={cn(
                  "w-full flex items-start gap-2 rounded-lg border px-3 py-2 text-left cursor-pointer transition-colors",
                  done
                    ? "border-emerald-500/35 bg-emerald-500/5"
                    : item.critical
                      ? "border-amber-500/35 bg-amber-500/5"
                      : "border-border/60 hover:bg-muted/30"
                )}
              >
                {done ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                ) : (
                  <Circle className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
                )}
                <span className="min-w-0">
                  <span className="block text-[12px] font-medium text-foreground">
                    {item.label}
                    {item.critical ? (
                      <span className="ml-1.5 text-[9px] uppercase text-amber-400 font-bold">
                        crítico
                      </span>
                    ) : null}
                  </span>
                  <span className="block text-[11px] text-muted-foreground leading-relaxed mt-0.5">
                    {item.detail}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
